import React, {
  Children,
  FC,
  MouseEvent,
  ReactElement,
  ReactNode,
  useMemo,
} from "react";
import { EditIcon } from "../../../icons";

import { LinkButton, TLinkButtonProps } from "../../ActionsSelectors/Button";
import { ICarousel } from "../../ActionsSelectors";
import { Row } from "../../Fundamentals/Grid";
import { RadioGroup } from "../../ActionsSelectors/RadioButton";

export interface IOptionSummaryProps {
  /** Text labels */
  labels?: {
    /** Title text */
    title?: string;
    /** End button's (edit button) text */
    endButton?: string;
  };

  /** HTML `name` for radio inputs */
  inputName?: string;

  /** Set initially selected option (could ne none) */
  selectedOption?: string;

  /** Custom button at the end of the title */
  endButton?: ReactNode | false;

  /** Custom icon for button at the end of the title */
  endButtonIcon?: Pick<TLinkButtonProps, "endIcon"> | false;

  /** Changes whether will several children elements be wrapped in a carousel or not */
  wrapInCarousel?: boolean;

  /** Amount of columns inside of widget */
  colsCount?: number;

  /** Additional params for carousel */
  carousel?: Partial<ICarousel>;

  /** Edit action link */
  editLink?: string;

  children?: ReactNode;

  /** On radio input selection callback */
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Edit action callback */
  onEdit?(event: MouseEvent): void;
}

const OptionSummary: FC<IOptionSummaryProps> = ({
  labels,
  inputName,
  selectedOption,
  editLink,
  carousel,
  wrapInCarousel,
  endButton,
  endButtonIcon,
  colsCount = 1,
  onChange,
  onEdit,
  children,
}) => {
  // Get length of `children` list
  const childrenSize = Children.count(children);

  /**
   * Transform `children` into iterable array. If there's a single element,
   * it still be an array with 1 element
   */
  let childrenList = Children.toArray(children);

  /**
   * If there is multiple items in list, transform each item's props automatically
   */

  if (childrenSize > 1) {
    childrenList = Children.map(children, (el) =>
      /** Set `OptionSummaryWidget` to render `RadioButton` */
      React.cloneElement(el as ReactElement, {
        withRadio: true,
      }),
    );
  }

  const iconButton = useMemo(() => {
    const clickHandler = (v) => {
      onEdit && onEdit(v);
    };
    return (
      endButton ?? (
        <LinkButton
          variant="icon"
          href={editLink}
          endIcon={(endButtonIcon as TLinkButtonProps["endIcon"]) ?? EditIcon}
          classes={{ iconRoot: "cursor-pointer" }}
          onClick={clickHandler}
        >
          {labels?.endButton}
        </LinkButton>
      )
    );
  }, [editLink, endButton, endButtonIcon, labels, onEdit]);

  return (
    <>
      {!endButton && !labels?.title ? null : (
        <div className="d-flex justify-content-between mb-7">
          {labels?.title ? (
            <div className="highlighter highlighter--passed">
              <span>{labels?.title}</span>
            </div>
          ) : null}
          {iconButton}
        </div>
      )}

      <RadioGroup
        name={inputName}
        selectedValue={selectedOption}
        onChange={onChange}
      >
        <Row classes={{ root: "uds-carousel-css" }}>
          {childrenList.map((child) => (
            <div
              className={`col-12 ${colsCount === 2 ? "col-md-6 mb-md-6" : ""}`}
            >
              {child}
            </div>
          ))}
        </Row>
      </RadioGroup>
    </>
  );
};

export default OptionSummary;
